import React, { useEffect, useRef } from 'react';

import {
  FieldArrayWithId,
  UseFormWatch,
  FieldArrayPath,
  UseFormSetValue,
  UseFieldArrayRemove,
  UseFormRegister,
  Control,
  FieldErrors,
  UseFieldArrayAppend,
} from 'react-hook-form';

import { SHARE_EXCHAGE_REQUEST_FORM_TITLE } from 'constants/share-exchange';
import { ConstraintVariants } from 'constants/shared';
import { ButtonVariants } from 'constants/shared/button';
import { TypographyVariants } from 'constants/shared/typography';
import {
  DealPredefinedData,
  FormItem,
  Option,
  StartShareExchangesInvestmentItem,
  StartShareExchangesInvestmentsFormValues,
  StartShareExchangeWatchValues,
} from 'interfaces';
import { selectUserEntityData } from 'modules/current-user/selectors';
import { useAppSelector } from 'modules/store';
import { Button, Constraint, TitleWithDescription } from 'shared-components';
import { getTitleWithoutMyWord } from 'utils';

import ShareExchangeDetailsSnippet from '../../deals/share-exchanges/ShareExchangeDetailsSnippet';
import ShareExchangeRequestFeesInfo from './ShareExchangeRequestFeesInfo';
import ShareExchangeRequestTransactionSummary from './ShareExchangeRequestTransactionSummary';

interface Props<
  TFieldValues extends { exchanges: StartShareExchangesInvestmentItem[] },
  TFieldArrayName extends FieldArrayPath<StartShareExchangesInvestmentItem>,
> {
  getFields: (
    watchValues: StartShareExchangeWatchValues,
    handleSetCompanyPrice: VoidFunction,
    classOfSharesOptions: Option[],
    closingDatesOptions: Option[],
    dealIndex: number,
  ) => FormItem[];
  dealsFields: FieldArrayWithId<TFieldValues, TFieldArrayName, 'id'>[];
  shareExchanges: StartShareExchangesInvestmentItem[];
  companyDealPredefinedData: DealPredefinedData | null;
  register: UseFormRegister<TFieldValues>;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  append: UseFieldArrayAppend<StartShareExchangesInvestmentsFormValues>;
  remove: UseFieldArrayRemove;
  isFormDisabled?: boolean;
  isLoading: boolean;
  onSubmit: VoidFunction;
}

const ShareExchangeRequestDetailsCommonFields = <
  TFieldValues extends { exchanges: StartShareExchangesInvestmentItem[] },
  TFieldArrayName extends FieldArrayPath<StartShareExchangesInvestmentItem>,
>({
  getFields,
  dealsFields,
  shareExchanges,
  companyDealPredefinedData,
  register,
  control,
  watch,
  errors,
  setValue,
  append,
  remove,
  isLoading,
  onSubmit,
}: Props<TFieldValues, TFieldArrayName>) => {
  const { isTypeEntity, entityName } = useAppSelector(selectUserEntityData);
  const endOfPageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Function to scroll to the end
    const scrollToEnd = () => {
      if (endOfPageRef.current) {
        endOfPageRef.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };
    // Delay to ensure all content is rendered
    const timer = setTimeout(scrollToEnd, 200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Constraint variant={ConstraintVariants.FULL_ROUNDED_LOWER} className='mt-10 pt-2'>
        <TitleWithDescription
          className='mb-1 3xl:mb-4'
          title={getTitleWithoutMyWord(SHARE_EXCHAGE_REQUEST_FORM_TITLE, isTypeEntity)}
          titleVariant={TypographyVariants.SEMI_LARGE}
        />
        <ShareExchangeDetailsSnippet
          getFields={getFields}
          dealsFields={dealsFields}
          shareExchanges={shareExchanges}
          companyDealPredefinedData={companyDealPredefinedData}
          register={register}
          control={control}
          watch={watch}
          errors={errors}
          setValue={setValue}
          append={append}
          remove={remove}
        />
      </Constraint>
      <Constraint variant={ConstraintVariants.FULL_ROUNDED_LOWER} className='mt-5 3xl:mt-10'>
        <TitleWithDescription
          className='mb-1 3xl:mb-4'
          title={'Transaction Summary'}
          titleVariant={TypographyVariants.SEMI_LARGE}
        />
        <ShareExchangeRequestTransactionSummary
          shareExchanges={shareExchanges}
          companyName={companyDealPredefinedData?.companyName}
        />
        <Button
          isLoading={isLoading}
          variant={ButtonVariants.PRIMARY}
          className='w-full my-5 3xl:my-6 '
          onClick={onSubmit}
          disabled={isLoading}
        >
          Initiate Share Exchange
        </Button>
        <div ref={endOfPageRef} />
        {companyDealPredefinedData?.fee && <ShareExchangeRequestFeesInfo fee={companyDealPredefinedData.fee} />}
      </Constraint>
    </>
  );
};

export default ShareExchangeRequestDetailsCommonFields;
